import { usePane, useWorkspace } from '@/components/layout/workspace/workspaceContext';
import type { RightPanelItem } from '@/components/layout/workspace/rightPanelContext';
import { useObserver } from '@/components/observer/observerContext';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import {
  Button,
  Deadline,
  EditableProperty,
  EntityChip,
  Money,
  PropertyList,
  PropertyRow,
  StatusControl,
} from '@/components/ui';
import { HOME, OWNERS, SAMPLE_NOW, STATUS_OPTIONS, type SampleRecord } from './model';
import { useRecords } from './recordsContext';

export function RecordDetails({ item }: { item: RightPanelItem }) {
  const ws = useWorkspace();
  const pane = usePane();
  const observer = useObserver();
  const { records, update, changeStatus } = useRecords();
  const record = records.find((r) => r.id === item.id);
  const anchor = useObserverAnchor('sample.panel.' + item.id, {
    role: 'block',
    label: record?.title ?? item.title,
  });
  if (!record)
    return <p className="text-sm text-fg-muted">Запись недоступна. Закрой панель и выбери другую.</p>;
  return (
    <div ref={anchor} data-observer-block="" className="flex flex-col gap-6">
      <PropertyList>
        <EditableProperty
          label="Название"
          value={record.title}
          validate={(v) => (!v ? 'Название не должно быть пустым' : undefined)}
          onCommit={(title) => update(record.id, { title })}
        />
        <PropertyRow label="Статус">
          <StatusControl
            value={record.status}
            options={STATUS_OPTIONS}
            onChange={(value) => changeStatus([record.id], value as SampleRecord['status'])}
          />
        </PropertyRow>
        <PropertyRow label="Сумма">
          <Money minorUnits={record.amount} currency={record.currency} />
        </PropertyRow>
        <PropertyRow label="Срок">
          <Deadline at={record.deadline} now={SAMPLE_NOW} />
        </PropertyRow>
        <PropertyRow label="Ответственный">
          <EntityChip
            kind="user"
            name={record.owner}
            onClick={() => ws.go(HOME + '/person/' + OWNERS.indexOf(record.owner), { pane })}
          />
        </PropertyRow>
        <EditableProperty
          label="Примечание"
          value={record.note}
          onCommit={(note) => update(record.id, { note })}
        />
      </PropertyList>
      <div className="rounded-md bg-muted p-4">
        <h3 className="mb-2 type-subheading">Связи</h3>
        <p className="text-xs text-fg-muted">Связанный человек открывается своей вкладкой в этом же окне.</p>
      </div>
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            observer.notify({
              kind: 'notification',
              anchorId: 'sample.panel.' + record.id,
              label: 'Свойства записи находятся здесь',
            })
          }
        >
          Позвать помощника к панели
        </Button>
      </div>
    </div>
  );
}
